/**
 * DatasetLabelingView Component
 *
 * A comprehensive labeling interface for datasets.
 * Allows viewing and labeling serial data with OK/NOK classifications.
 */

import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  ArrowLeft,
  Save,
  Check,
  X,
  Loader2,
  Tag,
  FileText,
  Filter,
} from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { Input } from "@/shared/ui/input";
import { Textarea } from "@/shared/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { Skeleton } from "@/shared/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Label } from "@/shared/ui/label";
import { cn } from "@/shared/lib/utils";
import {
  getDatasetById,
  getDatasetLabels,
  upsertDatasetLabels,
} from "../api/datasets";
import type {
  DatasetResponse,
  DatasetLabelsListResponse,
  SerialLabelSpan,
  DatasetLabelsUpsertItem,
} from "../types/dataset";

interface DatasetLabelingViewProps {
  datasetId: string;
  onBack: () => void;
}

type LabelFilter = "all" | "labeled" | "unlabeled" | "ok" | "nok";

export function DatasetLabelingView({ datasetId, onBack }: DatasetLabelingViewProps) {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<LabelFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [editingItem, setEditingItem] = useState<SerialLabelSpan | null>(null);
  const [editLabel, setEditLabel] = useState<string>("");
  const [editNote, setEditNote] = useState<string>("");
  const [pendingChanges, setPendingChanges] = useState<Map<string, DatasetLabelsUpsertItem>>(
    new Map()
  );

  // Fetch dataset details
  const {
    data: dataset,
    isLoading: loadingDataset,
  } = useQuery<DatasetResponse>({
    queryKey: ["ai", "dataset", datasetId],
    queryFn: () => getDatasetById(datasetId),
  });

  // Fetch dataset labels
  const {
    data: labelsData,
    isLoading: loadingLabels,
    error: labelsError,
  } = useQuery<DatasetLabelsListResponse>({
    queryKey: ["ai", "dataset", datasetId, "labels"],
    queryFn: () => getDatasetLabels(datasetId),
  });

  // Save labels mutation
  const { mutate: saveLabels, isPending: isSaving } = useMutation({
    mutationFn: (items: DatasetLabelsUpsertItem[]) =>
      upsertDatasetLabels(datasetId, { labels: items }),
    onSuccess: (result) => {
      toast.success(`Saved ${result.processedCount} label(s)`);
      queryClient.invalidateQueries({ queryKey: ["ai", "dataset", datasetId, "labels"] });
      setPendingChanges(new Map());
    },
    onError: (error) => {
      toast.error(`Failed to save labels: ${error.message}`);
    },
  });

  // Filter and search labels
  const filteredItems = useMemo(() => {
    if (!labelsData?.items) return [];

    let items = [...labelsData.items];

    // Apply pending changes for display
    items = items.map((item) => {
      const pending = pendingChanges.get(item.serial);
      if (pending) {
        return { ...item, label: pending.label, note: pending.note || item.note };
      }
      return item;
    });

    // Apply filter
    switch (filter) {
      case "labeled":
        items = items.filter((item) => item.label !== null);
        break;
      case "unlabeled":
        items = items.filter((item) => item.label === null);
        break;
      case "ok":
        items = items.filter((item) => item.label?.toLowerCase() === "ok");
        break;
      case "nok":
        items = items.filter((item) => item.label?.toLowerCase() === "nok");
        break;
    }

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      items = items.filter(
        (item) =>
          item.serial.toLowerCase().includes(query) ||
          item.label?.toLowerCase().includes(query) ||
          item.note?.toLowerCase().includes(query)
      );
    }

    return items;
  }, [labelsData?.items, filter, searchQuery, pendingChanges]);

  // Quick label handlers
  const handleQuickLabel = (serial: string, label: "OK" | "NOK") => {
    const existingItem = labelsData?.items.find((i) => i.serial === serial);
    const pending = pendingChanges.get(serial);

    setPendingChanges((prev) => {
      const next = new Map(prev);
      next.set(serial, {
        serial,
        label,
        note: pending?.note || existingItem?.note || null,
      });
      return next;
    });
  };

  // Open edit dialog
  const handleEditClick = (item: SerialLabelSpan) => {
    const pending = pendingChanges.get(item.serial);
    setEditingItem(item);
    setEditLabel(pending?.label || item.label || "");
    setEditNote(pending?.note || item.note || "");
  };

  // Save edit dialog
  const handleEditSave = () => {
    if (!editingItem) return;

    setPendingChanges((prev) => {
      const next = new Map(prev);
      next.set(editingItem.serial, {
        serial: editingItem.serial,
        label: editLabel || null,
        note: editNote || null,
      });
      return next;
    });

    setEditingItem(null);
  };

  // Save all pending changes
  const handleSaveAll = () => {
    const items = Array.from(pendingChanges.values());
    if (items.length === 0) {
      toast.info("No changes to save");
      return;
    }
    saveLabels(items);
  };

  // Calculate stats
  const stats = useMemo(() => {
    if (!labelsData) return { total: 0, labeled: 0, ok: 0, nok: 0 };

    const items = labelsData.items.map((item) => {
      const pending = pendingChanges.get(item.serial);
      return pending ? { ...item, label: pending.label } : item;
    });

    const labeled = items.filter((i) => i.label !== null).length;
    const ok = items.filter((i) => i.label?.toLowerCase() === "ok").length;
    const nok = items.filter((i) => i.label?.toLowerCase() === "nok").length;

    return {
      total: items.length,
      labeled,
      ok,
      nok,
    };
  }, [labelsData, pendingChanges]);

  const isLoading = loadingDataset || loadingLabels;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Tag className="h-5 w-5" />
              {loadingDataset ? (
                <Skeleton className="h-6 w-48" />
              ) : (
                dataset?.name || "Dataset Labeling"
              )}
            </h2>
            <p className="text-sm text-muted-foreground">
              Label samples as OK or NOK for model training
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {pendingChanges.size > 0 && (
            <Badge variant="secondary">
              {pendingChanges.size} unsaved change{pendingChanges.size !== 1 ? "s" : ""}
            </Badge>
          )}
          <Button
            onClick={handleSaveAll}
            disabled={pendingChanges.size === 0 || isSaving}
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold">{stats.total.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">Total Samples</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-chart-2">
                {stats.labeled.toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground">Labeled</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-success">
                {stats.ok.toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground">OK Samples</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-destructive">
                {stats.nok.toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground">NOK Samples</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={filter} onValueChange={(v) => setFilter(v as LabelFilter)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Samples</SelectItem>
              <SelectItem value="labeled">Labeled</SelectItem>
              <SelectItem value="unlabeled">Unlabeled</SelectItem>
              <SelectItem value="ok">OK Only</SelectItem>
              <SelectItem value="nok">NOK Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Input
          placeholder="Search by serial, label, or note..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-sm"
        />
        <div className="ml-auto text-sm text-muted-foreground">
          Showing {filteredItems.length} of {stats.total} samples
        </div>
      </div>

      {/* Labels Table */}
      <Card>
        <ScrollArea className="h-[500px]">
          <Table>
            <TableHeader className="sticky top-0 bg-background">
              <TableRow>
                <TableHead className="w-[200px]">Serial</TableHead>
                <TableHead>Start Time</TableHead>
                <TableHead>End Time</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Note</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-28" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-28" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-40" /></TableCell>
                    <TableCell><Skeleton className="h-8 w-24 ml-auto" /></TableCell>
                  </TableRow>
                ))
              ) : labelsError ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-48 text-center text-destructive">
                    Failed to load labels: {(labelsError as Error).message}
                  </TableCell>
                </TableRow>
              ) : filteredItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-48 text-center text-muted-foreground">
                    <FileText className="mx-auto h-12 w-12 opacity-30 mb-4" />
                    No samples found
                  </TableCell>
                </TableRow>
              ) : (
                filteredItems.map((item) => {
                  const pending = pendingChanges.get(item.serial);
                  const displayLabel = pending?.label ?? item.label;
                  const hasChanges = pending !== undefined;

                  return (
                    <TableRow
                      key={item.serial}
                      className={cn(hasChanges && "bg-warning/5")}
                    >
                      <TableCell className="font-mono text-sm">
                        {item.serial}
                        {hasChanges && (
                          <Badge variant="outline" className="ml-2 text-xs">
                            Modified
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.start
                          ? format(new Date(item.start), "MMM d, HH:mm:ss")
                          : "—"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.end
                          ? format(new Date(item.end), "MMM d, HH:mm:ss")
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {displayLabel ? (
                          <Badge
                            variant={
                              displayLabel.toLowerCase() === "ok"
                                ? "default"
                                : "destructive"
                            }
                          >
                            {displayLabel}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                        {pending?.note ?? item.note ?? "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant={displayLabel === "OK" ? "default" : "outline"}
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => handleQuickLabel(item.serial, "OK")}
                          >
                            <Check className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant={displayLabel === "NOK" ? "destructive" : "outline"}
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => handleQuickLabel(item.serial, "NOK")}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClick(item)}
                          >
                            Edit
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editingItem} onOpenChange={() => setEditingItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Label</DialogTitle>
            <DialogDescription>
              Serial: <code className="font-mono">{editingItem?.serial}</code>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Label</Label>
              <Select value={editLabel} onValueChange={setEditLabel}>
                <SelectTrigger>
                  <SelectValue placeholder="Select label" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="OK">OK</SelectItem>
                  <SelectItem value="NOK">NOK</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Note (optional)</Label>
              <Textarea
                placeholder="Add a note about this sample..."
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingItem(null)}>
              Cancel
            </Button>
            <Button onClick={handleEditSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default DatasetLabelingView;
